import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Activity, BadgeCheck, BookOpen, Monitor, Battery, DollarSign, CheckCircle, FileText, ArrowRight, Shield } from 'lucide-react';

const PowerPlants = () => {
  const keyChallenges = [
    {
      title: 'Grid Modernization & BESS Support',
      description: 'Proactive air permitting for the auxiliary emission sources required by utility-scale Battery Energy Storage Systems (BESS), ensuring new infrastructure meets Alaska DEC and EPA standards.',
      icon: Battery
    },
    {
      title: 'Federal Funding Compliance',
      description: 'Strategic environmental oversight for federally funded infrastructure, providing the air quality documentation required for USDA PACE, New ERA, and other grant programs.',
      icon: DollarSign
    },
    {
      title: 'Strategic Permit Engineering',
      description: 'From Title V renewals to precision "Synthetic Minor" modifications, we engineer permit conditions that maximize operational flexibility while minimizing regulatory exposure.',
      icon: FileText
    },
    {
      title: 'SIP & Nonattainment Expertise',
      description: 'Specialized navigation of the Fairbanks North Star Borough\'s Serious Nonattainment SIP, ensuring BACT compliance for assets operating in high-scrutiny air sheds.',
      icon: Shield
    },
    {
      title: 'Asset Lifecycle Management',
      description: 'Technical management for the full lifecycle of power equipment, including NSPS Subpart KKKK (Turbines), IIII/JJJJ (Engines), and RICE NESHAP ZZZZ.',
      icon: BadgeCheck
    },
    {
      title: 'Continuous Compliance Systems',
      description: 'Developing auditable compliance systems that streamline semi-annual reporting, deviation tracking, and Annual Compliance Certifications (ACC).',
      icon: Monitor
    }
  ];

  const regulations = [
    {
      title: '40 CFR 60 - New Source Performance Standards (NSPS)',
      subparts: [
        { name: 'Subpart A', description: 'General provisions applicable to all NSPS sources including definitions, compliance requirements, performance testing, and monitoring.' },
        { name: 'Subpart J', description: 'Standards for fossil-fuel fired steam generating units, including emission limits for SO2, NOx, and particulate matter.' },
        { name: 'Subpart GG', description: 'Stationary gas turbines with heat input ≥10 MMBtu/hr. NOx limits: 150 ppm at 15% O2 for gas-fired turbines.' },
        { name: 'Subpart IIII', description: 'Compression ignition internal combustion engines with emission limits for NOx, PM, CO, and hydrocarbons.' },
        { name: 'Subpart KKKK', description: 'Combustion turbines ≥10 MMBtu/hr constructed after 2005. NOx: 25 ppm, CO: 130 ppm at 15% O2.' },
      ],
    },
    {
      title: '40 CFR 63 - National Emission Standards for Hazardous Air Pollutants (NESHAP)',
      subparts: [
        { name: 'Subpart ZZZZ', description: 'Standards for stationary reciprocating internal combustion engines with emission limits for formaldehyde, acrolein, methanol, and other HAPs.' },
        { name: 'Subpart YYYY', description: 'Standards for stationary combustion turbines with emission limits for formaldehyde and other HAPs specific to turbines.' },
      ],
    },
    {
      title: '40 CFR 64 - Compliance Assurance Monitoring (CAM)',
      description: 'Required for emission units with add-on control devices (e.g., Selective Catalytic Reduction for NOx) when pre-control emissions exceed 100 TPY.',
    },
  ];

  const plantTypes = [
    {
      type: 'Major Source & Railbelt Utilities',
      definition: 'Utilities operating in complex regulatory environments requiring sophisticated permitting and compliance strategies.',
      requirements: ['Title V Permit Renewal & Negotiation', 'PSD/NSR Permit Applicability Analysis', 'SIP & Serious Nonattainment Area Compliance', 'GHG Reporting (40 CFR 98) & Sustainability Support'],
    },
    {
      type: 'Remote, Industrial & Minor Sources',
      definition: 'Independent power producers and remote facilities requiring strategic permitting to avoid unnecessary regulatory classifications.',
      requirements: ['Title V & Synthetic Minor Permitting', 'NSPS Subpart KKKK (Turbines) & IIII/JJJJ (Engines)', 'RICE NESHAP (Subpart ZZZZ) Management', 'Owner-Requested Limits (ORL) for Administrative Efficiency'],
    },
  ];

  return (
    <div className="min-h-screen" data-testid="power-plants-page">
      {/* Hero Section - Control Room with Dark Gradient */}
      <section
        className="relative text-white py-20"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_22bc7bcc-5e67-46b7-9a44-0a703af52499/artifacts/t84ry60r_Power%20Plant%20Control%20Room.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white drop-shadow-lg" data-testid="page-title">
              Securing Operational Certainty for Alaska's Energy Future
            </h1>
            <p className="text-2xl text-gray-100 mb-8 drop-shadow-md">
              Specialized air quality permitting and compliance strategy for the Railbelt's evolving grid, Arctic industrial power, and remote utility infrastructure. We navigate regulatory complexity so you can focus on generation.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-lg"
              >
                Discuss Your Compliance Strategy
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <a
                href="#key-challenges"
                className="inline-flex items-center px-6 py-3 bg-white text-red-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                View Our Capabilities
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-left">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Alaska's Power Generation Landscape</h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Alaska's energy landscape requires hyper-localized regulatory expertise. Raven Environmental provides specialized oversight across four distinct power segments:
            </p>
            
            <div className="space-y-4 mb-6">
              <p className="text-base text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Interconnected Railbelt Utilities:</span> Partnering with major cooperatives and independent power producers to navigate the transition to hybrid renewables, BESS integration, and complex air permitting in nonattainment zones.
              </p>
              
              <p className="text-base text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Arctic Industrial Microgrids:</span> Providing Title V and NSPS Subpart KKKK technical support for the high-capacity turbine infrastructure driving Alaska's resource extraction sector.
              </p>
              
              <p className="text-base text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Southeast Hydro-Thermal Systems:</span> Managing air permitting for the critical "diesel firming" and emergency backup assets that stabilize hydro-dominant regional grids.
              </p>
              
              <p className="text-base text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Remote & Isolated Microgrids:</span> Streamlining compliance for village power associations through "Synthetic Minor" strategies and Owner-Requested Limits (ORL) to reduce administrative burdens.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Challenges - Crimson Jewel Icons */}
      <section id="key-challenges" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Key Compliance Challenges
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl">
              Strategic air quality compliance for Alaska's evolving power generation infrastructure
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keyChallenges.map((challenge, index) => {
              const IconComponent = challenge.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border-t-4 border-red-600 hover:-translate-y-1">
                  {/* Jewel Style Icon - Crimson */}
                  <div className="bg-red-600 w-16 h-16 rounded-lg flex items-center justify-center shadow-lg mb-4">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{challenge.title}</h3>
                  <p className="text-gray-600">{challenge.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plant Types */}
      <section className="py-16 bg-white" data-testid="plant-types-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-left">Power Plant Classifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {plantTypes.map((plant, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-blue-600">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{plant.type}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{plant.definition}</p>
                <h4 className="font-semibold text-gray-900 mb-3">Key Requirements:</h4>
                <ul className="space-y-2">
                  {plant.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory Requirements */}
      <section className="py-16 bg-white" data-testid="regulations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-left">Federal Regulatory Requirements</h2>
          <div className="space-y-8">
            {regulations.map((reg, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-lg border-l-4 border-blue-600">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{reg.title}</h3>
                {reg.description && (
                  <p className="text-gray-600 mb-6 leading-relaxed">{reg.description}</p>
                )}
                {reg.subparts && (
                  <div className="space-y-4">
                    {reg.subparts.map((subpart, idx) => (
                      <div key={idx} className="bg-white p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">{subpart.name}</h4>
                        <p className="text-gray-600 text-sm">{subpart.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAM Requirements */}
      <section className="py-16 bg-gray-50" data-testid="cam-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Compliance Assurance Monitoring (CAM)</h2>
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Requirements</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Monitoring design criteria for control devices</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Performance criteria to detect deviations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Quality assurance and calibration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Detailed recordkeeping (5-year retention)</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Implementation Steps</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-sm">1</div>
                    <span className="text-gray-700">Initial assessment of control devices</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-sm">2</div>
                    <span className="text-gray-700">Design monitoring systems</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-sm">3</div>
                    <span className="text-gray-700">Personnel training</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-sm">4</div>
                    <span className="text-gray-700">Continuous monitoring and documentation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Title I Permits */}
      <section className="py-16 bg-white" data-testid="title-i-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 p-8 rounded-lg border-2 border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <FileText className="w-8 h-8 text-blue-600 mr-3" />
              Title I Permit Requirements
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Permittees must comply with all stationary source-specific requirements including:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Best Available Control Technology (BACT) limits</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">EPA Prevention of Significant Deterioration (PSD) permits</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">State Implementation Plan (SIP)-approved permits</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Owner-requested limits under Alaska regulations</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-700 text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <h2 className="text-3xl font-bold mb-6">Need Expert Assistance with Power Plant Permitting?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Our team has extensive experience with Alaska's power generation regulations and can guide you through every step of the compliance process.
          </p>
          <Link
            to="/contact/power-plants"
            data-testid="cta-button"
            className="inline-flex items-center px-8 py-4 bg-white text-blue-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            Start Your Power Plant Inquiry
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PowerPlants;
